import type {
  AxiosInstance,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosRequestTransformer,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import axios, { AxiosError } from "axios";
import { isArray, isPlainObject } from "lodash-es";

import { rpcUrl, SERVER_PATH } from "@/backend/impl/HttpRpcBackend";
import { getServerInfo, loggedIn } from "@/backend/read/BackendReader";
import { CancelError } from "@/error/CancelError";
import { captureMessage } from "@/error/sentry";
import type { LogoutReason } from "@/router/types";
import { useClientStore } from "@/store/client";
import { useConnectionStore } from "@/store/connection";
import { authAPIUrl, isCloud, loginTimeout } from "@/utils/env/Environment";

import { BackendError } from "../BackendError";
import { NoSuccessError } from "../NoSuccessError";
import type { UserResponse } from "../serverModel";
import type { LoginRequest, LoginResponse } from "./auth";
import {
  assertResolve,
  assertServerInfo,
  callBackend,
  loadImage,
  settledToString,
} from "./lowlevel";

const DEFAULT_RETRIES = 1;
const CONNECT_TIMEOUT = 2000;

export type ExtendedRequestConfig = AxiosRequestConfig & ExtendedRequest;

interface ExtendedRequest {
  sentAt: string;
  retry: number;
  maxRetries: number;
  reachable: ReachType[];
  loggedIn: boolean;
}

interface ReachType {
  google: string;
  http: string;
  ws: string;
}

let refreshing: Promise<UserResponse> | null = null;

export function createApiClient(url: string, options?: Partial<AxiosRequestConfig>) {
  const client = axios.create({
    baseURL: url,
    withCredentials: true,
    timeout: 10000,
    transformRequest,
    ...options,
  });
  return enhanceRequestInterceptor(networkErrorInterceptor(authenticationInterceptor(client)));
}

export function modernInterceptor(client: AxiosInstance, options: { defaultValue?: unknown } = {}) {
  client.interceptors.response.use((res) => {
    if (res.data.status !== "success") {
      const contexts = {
        request: requestData(res.config as ExtendedRequestConfig),
        response: responseData(res),
      };

      if ("defaultValue" in options) {
        captureMessage("Unsuccessful request", contexts);
        res.data = options.defaultValue;
        return res;
      }

      throw new NoSuccessError(
        res.data.error?.message || res.data.error?.code || "unknown",
        res.config.url || "unknown",
        contexts,
      );
    }
    res.data = res.data.data;
    return res;
  });
  return client;
}

function enhanceRequestInterceptor(client: AxiosInstance) {
  client.interceptors.request.use((request) => {
    const req = request as ExtendedRequestConfig;
    return {
      ...request,
      sentAt: new Date().toISOString(),
      retry: req.retry || 0,
      maxRetries: req.maxRetries ?? DEFAULT_RETRIES,
      reachable: req.reachable ?? [],
      loggedIn: loggedIn(),
    };
  });
  return client;
}

function networkErrorInterceptor(client: AxiosInstance) {
  client.interceptors.response.use(
    (response) => response,
    async (fail) => {
      if (isNetworkError(fail)) {
        if (!useConnectionStore().netOnline) {
          throw new CancelError("Network problem: Browser is offline", fail);
        }
        // the browser thinks it's online, but we could not reach our server -> try to reach google and server info.
        const [google, http, ws] = await Promise.allSettled([
          assertResolve(() =>
            isCloud
              ? loadImage("https://www.google.com/favicon.ico", CONNECT_TIMEOUT)
              : Promise.resolve(),
          ),
          assertServerInfo(() =>
            callBackend(rpcUrl(SERVER_PATH + "get_server_info", []), CONNECT_TIMEOUT),
          ),
          assertServerInfo(() => getServerInfo(false, CONNECT_TIMEOUT)),
        ]);
        fail.config.reachable.push({
          google: settledToString(google),
          http: settledToString(http),
          ws: settledToString(ws),
        });
        if (fail.config.retry < fail.config.maxRetries) {
          fail.config.retry++;
          // using client here would execute any response interceptors twice
          return rawClient.request(fail.config);
        }
        const contexts = {
          request: isPlainObject(fail.config) ? requestData(fail.config) : {},
          response: isPlainObject(fail.response)
            ? responseData(fail.response)
            : { code: fail.code, message: fail.message },
        };
        throw new BackendError("Network problem", fail.message, contexts);
      }
      throw fail;
    },
  );
  return client;
}

function authenticationInterceptor(client: AxiosInstance) {
  client.interceptors.response.use(
    (response) => response,
    async (fail) => {
      if (fail.response?.status === 401) {
        const data = fail.response.data;
        switch (data?.error?.code) {
          case "UNAUTHENTICATED":
            // This happens primarily when the user is not logged in at all (e.g. initial of the app)
            return redirectToLogin("unauthenticated");
          case "TOKEN_EXPIRED":
            // if we already are refreshing the token, don't refresh again,
            // but wait for the refresh to end before repeating the request
            try {
              await refreshTokenGuarded({ correlationId: fail.config.headers["correlation-id"] });
            } catch (_) {
              // Token refresh failed; redirect user to login page
              redirectToLogin("refresh-failed");
            }
            return await rawClient.request(fail.config);
        }
        // If for some reason a 401 does not contain an error-code
        return redirectToLogin(data?.error?.code || "unknown");
      }
      throw fail;
    },
  );
  return client;
}

function requestData(config: ExtendedRequestConfig) {
  return {
    baseUrl: config.baseURL,
    url: config.url,
    headers: config.headers,
    sentAt: config.sentAt,
    retry: config.retry,
    maxRetries: config.maxRetries,
    reachable: config.reachable,
    loggedIn: config.loggedIn,
  };
}

function responseData(res: AxiosResponse) {
  return {
    status: res.status,
    statusText: res.statusText,
    code: res.data.status,
    message: res.data.error?.code || "unknown",
    details: res.data.data,
    data: res.data,
  };
}

function redirectToLogin(reason: string): never {
  window.location.assign(
    `${authAPIUrl}/auth/login?type=app&path=${encodeURIComponent(location.hash || "")}&reason=${reason}`,
  );
  throw new CancelError("Unauthorized");
}

function transformRequest(
  this: InternalAxiosRequestConfig,
  data: unknown,
  headers: AxiosRequestHeaders,
) {
  if (headers && !headers["correlation-id"]) {
    headers["correlation-id"] = useClientStore().newCorrelationId();
  }
  for (const transform of defaultTransformers()) {
    data = transform.call(this, data, headers);
  }
  return data;
}

function defaultTransformers(): AxiosRequestTransformer[] {
  const transformers = axios.defaults.transformRequest;
  if (isArray(transformers)) {
    return transformers;
  }
  if (transformers) {
    return [transformers];
  }
  return [];
}

const rawClient = enhanceRequestInterceptor(
  networkErrorInterceptor(
    axios.create({
      baseURL: authAPIUrl,
      withCredentials: true,
      timeout: 10000,
      transformRequest,
    }),
  ),
);

// login and loginPage can never return TOKEN_EXPIRED
export async function login(params: LoginRequest): AxiosPromise<LoginResponse> {
  return await rawClient.get<LoginResponse>("/auth/login", { params, timeout: loginTimeout });
}

export function loginPage(params: LoginRequest): AxiosPromise<LoginResponse> {
  return rawClient.get<LoginResponse>("/auth/loginPage", { params });
}

// using createApiClient for refreshToken or logout could lead to endless loop
export async function refreshToken(config: {
  path?: string;
  correlationId?: string;
}): Promise<UserResponse> {
  const res = await rawClient.get<UserResponse>(
    `auth/token/refresh?path=${encodeURIComponent(config.path || "")}`,
    { headers: { "correlation-id": config.correlationId || "" } },
  );
  return res.data;
}

// Deduplicates concurrent refresh calls — if a refresh is already in-flight, callers wait for it
// rather than firing a second request with the same (potentially rotated) refresh token.
export async function refreshTokenGuarded(
  config: { path?: string; correlationId?: string } = {},
): Promise<UserResponse> {
  try {
    if (!refreshing) {
      refreshing = refreshToken(config);
    }
    return await refreshing;
  } finally {
    refreshing = null;
  }
}

export function logout(reason: LogoutReason) {
  return rawClient.get<{ redirectUrl?: string }>("/auth/logout", { params: { reason } });
}

export async function isAuthenticated(): Promise<UserResponse> {
  const res = await rawClient.get<UserResponse>(`auth/user`);
  return res.data;
}

function isNetworkError(err: AxiosError) {
  return err?.code === AxiosError.ECONNABORTED || err?.code === AxiosError.ERR_NETWORK;
}
